import { Button, Icon, CopySVG } from "@newsela/angelou";
import { useRouter } from "next/router";

import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
} from "@newsela/snitch";
import useAssignmentCreate from "@/assignments/useAssignmentCreate";
import {
  getErrorToastProps,
  getSuccessToastProps,
} from "@/assignments/utils/assignmentToastHelpers";
import { formatAssignmentPayload } from "@/assignments/utils/formatAssignmentPayload";
import globalToasterRef from "@/base/globalToasterRef";
import { Assignment } from "@/types/models/Assignment.interface";
import { StreamProperties } from "@/types/models/EventProperties.interface";
import stringifyParams from "@/utils/stringifyParams";
import ActionsDropdown from "@/view/ActionsDropdown";
import {
  dropdownItemCss,
  copyIconCss,
} from "./DuplicateAssignmentOption.style";

interface DuplicateOption {
  updatedAssignment: Pick<
    Assignment,
    "assignment_members" | "content_id" | "is_draft"
  >;
  streamProperties: StreamProperties;
  originalAssignment: Assignment;
}

const DuplicateAssignmentOption = ({
  updatedAssignment,
  streamProperties,
  originalAssignment,
}: DuplicateOption) => {
  const assignmentDuplicateEventProperties = {
    objectType: "Assignment",
    actionPrefix: "Initiate",
    actionName: "Copy",
    streamProperties: {
      ...streamProperties,
      description: "Copy assignment",
    },
  };
  // Toasts
  const successMessage = "A duplicate was created in a new tab.";
  const [createAssignment] = useAssignmentCreate();
  const router = useRouter();
  const { query } = router;
  const { levelId: currentContentLevelId } = query as {
    levelId: string;
  };
  const { trackAction, getEventName } = useTracker();

  const duplicateSuccessToastProps = getSuccessToastProps(
    successMessage,
    currentContentLevelId,
    updatedAssignment?.content_id,
  );

  const saveErrorToastProps = getErrorToastProps(
    "Something went wrong duplicating your assignment. Please try again.",
  );

  const handleDuplicate = async () => {
    const { contentId } = router?.query;
    const formattedContentId = contentId?.toString();
    const formattedAssignment = formatAssignmentPayload(
      updatedAssignment,
      updatedAssignment?.is_draft,
      originalAssignment,
    );

    // We want to duplicate assignment regardless of whether or not the original assignment is successfully saved/updated.
    await createAssignment(formattedAssignment);

    try {
      window.open(getDuplicateHref(formattedContentId));
      globalToasterRef.current?.displayToast(duplicateSuccessToastProps);
      trackAction({
        event_type: eventTypes.click,
        event_name: getEventName(
          objectTypes.assignment,
          actionPrefixes.initiate,
          actionNames.create,
        ),
        dimension_properties: {
          content_type: "Article",
          content_id: currentContentLevelId,
          object_id: updatedAssignment?.content_id,
          description: "Open copy of assignment in new tab",
        },
      });
    } catch (_error) {
      return globalToasterRef.current.displayToast(saveErrorToastProps);
    }
  };

  const getDuplicateHref = (contentId: string | undefined | null) => {
    if (!contentId)
      throw new Error("Unable to construct duplicate assignment URL.");

    const queryParams = {
      ...query,
      selectedAssignmentId: "null",
      copiedAssignmentId: updatedAssignment.content_id,
    };

    const stringifiedParams = stringifyParams(queryParams);

    return `/view/${contentId}/${stringifiedParams}`;
  };

  return (
    <ActionsDropdown.ButtonItem
      __cssFor={{ Button: { root: dropdownItemCss } }}
      onClick={() => {
        void handleDuplicate();
      }}
      dataProps={{
        "data-qa-selector": "assignment-dropdown-duplicate-assignment",
        "data-testid": "assignment-dropdown-button",
      }}
    >
      <Icon
        isDecorative={false}
        __cssFor={{ root: copyIconCss }}
        size={Button.sizes.md}
        SvgComponent={CopySVG}
        title="Duplicate assignment"
        {...assignmentDuplicateEventProperties}
      />
      Duplicate assignment
    </ActionsDropdown.ButtonItem>
  );
};

export default DuplicateAssignmentOption;
